import React, {useEffect, useMemo, useState} from 'react';
import {message} from "antd";
import {CustomCard} from "../../../../../../components/UI/AntCustom/CustomCard";
import {CustomButton} from "../../../../../../components/UI/AntCustom/CustomButton";
import styled from "styled-components";
import {useAppDispatch, useAppSelector} from "../../../../../../store/store";
import {ProfileManagerCard} from "./ProfileManagerCard";
import {
  clearManagerList,
  getManagerList
} from "../../../../../../store/reducers/personnelManagementReducer/personnelManagementReducer";
import {getTariff, clearTariff} from "../../../../../../store/reducers/tariffReducer/tariffReducer";
import {InitialPage} from "../../../../../../utils/constans/pagination/pagination";
import {Loader} from "../../../../../../components/UI/Loader/Loader";
import {NoData} from "../../../../../../components/UI/NoData/NoData";
import {AddManagerModal} from "./AddManagerModal";
import {defineMessages, useIntl} from "react-intl";
import {CommonFormMessages} from "../../../../../../intl/commonMessages/CommonFormMessages";
import {mediaScreenSizes} from "../../../../../../utils/constans/styles/screenSizes";
import {useIsMobile} from "../../../../../../hooks/useWindowSize";
import {Role} from "../../../../../../config/acl/roles/Role";
import {groupFreighter, groupCharterer} from "../../../../../../config/acl/roles/groups";

const PersonnelContent = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(auto-fill, minmax(363px, 1fr));
  
  @media(max-width: ${mediaScreenSizes.mobile}) {
    grid-template-columns: repeat(auto-fill, minmax(266px, 1fr));
  }
`;


const ProfilePersonnelMessages = defineMessages({
  addManager: {
    id: "profilePersonnel.addManager",
    defaultMessage: "Add user",
  },
  noDataTitle: {
    id: "profilePersonnel.noDataTitle",
    defaultMessage: "You don't have any personnel yet. You can add users by creating separate accounts for them",
  },
  noDataAction: {
    id: "profilePersonnel.noDataAction",
    defaultMessage: "Add user",
  },
  title: {
    id: "profilePersonnel.title",
    defaultMessage: "Users",
  },
  trialManagerLimitReached: {
    id: "addManagerModal.trialManagerLimitReached",
    defaultMessage: "Trial mode allows adding up to 7 users. Please upgrade your subscription to add more.",
  },
  seatLimitReached: {
    id: "addManagerModal.seatLimitReached",
    defaultMessage: "You have reached your seat limit. Ask your company administrator to buy more seats in Subscription.",
  },
})

const TRIAL_MANAGER_LIMIT = 7;

export const ProfilePersonnel = () => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const isMobile = useIsMobile();
  const managers = useAppSelector(state => state.personnelManagement.itemsObject.items);
  const user = useAppSelector(state => state.user.oneItemObject.item);
  const tariff = useAppSelector(state => state.tariff.oneItemObject.item);
  const isTariffLoading = useAppSelector(state => state.tariff.oneItemObject.isLoading);
  const [currentPage, setCurrentPage] = useState<number>(InitialPage);
  const [isAddManagerModalOpen, setIsAddManagerModalOpen] = useState<boolean>(false);
  const {
    items,
    isLoading,
    totalCount,
  } = useAppSelector(state => state.personnelManagement.itemsObject);

  const isTrialLimitReached = useMemo(() => {
    const activeTrialPackage = tariff?.companyTariffPackages?.find(pkg => pkg.active && pkg.isTrial);
    if (!activeTrialPackage) return false;
    const isOperator = groupFreighter.includes(user.role);
    const isBroker = groupCharterer.includes(user.role);
    if (isOperator) {
      const operatorManagers = managers.filter(m =>
        m.role === Role.freighterClientManager || m.role === Role.freighterServiceManager
      );
      return operatorManagers.length >= TRIAL_MANAGER_LIMIT;
    }
    if (isBroker) {
      const brokerManagers = managers.filter(m => m.role === Role.chartererManager);
      return brokerManagers.length >= TRIAL_MANAGER_LIMIT;
    }
    return false;
  }, [tariff, user.role, managers]);

  const isSeatLimitReached = useMemo(() => {
    // Find active package with subscribed seats (trialing or paid - both use seat limits from Stripe).
    // billedUsersCount: brokers count admin as 1 seat; operators exclude admin.
    const activePackageWithSeats = tariff?.companyTariffPackages?.find(
      pkg => pkg.active && (pkg.subscribedUsersCount ?? 0) >= 1
    );
    if (!activePackageWithSeats) return false;
    const subscribed = activePackageWithSeats.subscribedUsersCount ?? 0;
    const billed = activePackageWithSeats.billedUsersCount ?? 0;
    return billed >= subscribed;
  }, [tariff]);

  const handleAddManagerClick = () => {
    if (isTariffLoading) {
      message.info(intl.formatMessage({ id: 'common.loading', defaultMessage: 'Please wait...' }));
      return;
    }
    if (isTrialLimitReached) {
      message.warning(intl.formatMessage(ProfilePersonnelMessages.trialManagerLimitReached));
      return;
    }
    if (isSeatLimitReached) {
      message.warning(intl.formatMessage(ProfilePersonnelMessages.seatLimitReached));
      return;
    }
    setIsAddManagerModalOpen(true);
  };

  useEffect(() => {
    dispatch(getManagerList({
      page: currentPage,
    }));
    dispatch(getTariff({}));

    return () => {
      dispatch(clearManagerList());
      dispatch(clearTariff());
    }
  }, []);

  const loadMore = () => {
    dispatch(getManagerList({
      page: currentPage + 1,
    }));

    setCurrentPage(prev => prev + 1);
  }


  const extra = <CustomButton heightSize={'s'} ghost={true} type={'primary'} onClick={handleAddManagerClick}>+{intl.formatMessage(ProfilePersonnelMessages.addManager)}</CustomButton>

  const noData = <NoData
    title={intl.formatMessage(ProfilePersonnelMessages.noDataTitle)}
    actionButton={{
      title: `+${intl.formatMessage(ProfilePersonnelMessages.noDataAction)}`,
      action: handleAddManagerClick
    }}
  />

  return (
    <CustomCard title={intl.formatMessage(ProfilePersonnelMessages.title)} width={'max'} isContainerCard={true} extra={extra} titlePosition={'space-between'} style={{minHeight: 315}}>
      {items.length === 0
        ? (isLoading ? <Loader/> : noData)
        : <PersonnelContent>
          {managers.map(m => <ProfileManagerCard key={m.id} manager={m}/>)}
        </PersonnelContent>
      }

      {items.length > 0 && totalCount > items.length &&
        <CustomButton
          loading={isLoading}
          type={'primary'}
          onClick={loadMore}
        >
          {intl.formatMessage(CommonFormMessages.showMore)}
        </CustomButton>}

      {totalCount > 0 && isMobile && <CustomButton type={'primary'} onClick={handleAddManagerClick}>{intl.formatMessage(ProfilePersonnelMessages.addManager)}</CustomButton>}

      {isAddManagerModalOpen &&
        <AddManagerModal
          isModalVisible={isAddManagerModalOpen}
          setIsModalVisible={setIsAddManagerModalOpen}
        />}
    </CustomCard>
  );
};
